import { WalletAddress } from "./Ether";
import { ShortnerShare } from "./Shortner";

export type StoryIndex = number | string;

/**
 * @description Return type for saveStory endpoint
 */
export interface SaveStoryResponse {
  storyIndex?: StoryIndex;
}

/**
 * @description Return type for saveStoryForShare endpoint
 */
 export interface SaveStoryForShareResponse extends SaveStoryResponse {
  storyId?: number;
  shareInfo: ShortnerShare;
  renderUrlToAwait: string;
}

/**
 * @description Information that shows when picking a story (old plot page)
 */
export interface StoryListing {
  title: string;
}

/**
 * @description Keep data about story listings (shown in the beginning of the app)
 * Made as an object so we can extend in case of need
 */
 export interface StoriesData {
  storiesListing: Record<number, StoryListing>;
}

/** story data in s3 is nested under one of 'stories' or 'templates' prefix */
export type StoryKeyPrefix = 'stories' | 'templates';

/** add infix to paths */
export type StoryKeyInfix = 'nfts';

/** @description used as a resource locator for stories stored in S3 and served by
 *  the svs-publisher pipeline backend
 *
 * The story key is composed of an address and an id.  The address is either a user's wallet
 * or the 0x0 address for anonymous shares.
 *
 * The id is either
 *   1. For user wallet stories, the id is a sequential numeric id starting from 1 for the first story
 *   2. For anonymous shares, the id is a sha256 hash of the story content, represented as a hext-encoded utf8 string
 */
export class StoryKey {
    prefix: StoryKeyPrefix;
    address: string;
    id: string;
    suffix: string;
    infix?: StoryKeyInfix;

    constructor(prefix: StoryKeyPrefix, address: string, id: string | number, suffix = 'json', infix?: StoryKeyInfix) {
        this.prefix = prefix;
        this.suffix = suffix;
        this.infix = infix;
        this.address = address;
        this.id = id.toString();
    }

    /** @return the full story key path */
    toString() {
        let suffix = this.suffix
            ? '.' + this.suffix
            : '';
        if(this.address !== '0x0' && suffix === '.json') {
            suffix = `/dialog${suffix}`;
        }
        return `${this.prefix}/${this.rawKey()}${suffix}`;
    }

    /** @returns the address and id, without prefix or suffix */
    rawKey():string {
        if(this.infix) {
            return `${this.address}/${this.infix}/${this.id}`;
        }
        return `${this.address}/${this.id}`;
    }

    /** @returns a copy of this storyKey with a different prefix */
    withPrefix(prefix: StoryKeyPrefix) {
        return new StoryKey(prefix, this.address, this.id, this.suffix);
    }

    /** @returns a copy of this storyKey with a different suffix */
    withSuffix(suffix: string) {
        return new StoryKey(this.prefix, this.address, this.id, suffix);
    }
}


export function storyUrlFormat(viewerUrl:string, walletAddress:WalletAddress, storyId:string|number, tokenId:string):string {
    return `https://${viewerUrl}/${walletAddress}/n/${storyId}/${tokenId}`;
}
